import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectToken } from "../../store/authSlice";
import { selectActiveProjectId } from "../../store/projectSlice";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

/**
 * Reusable Excel download button.
 *
 * Props:
 *   endpoint  — API path, e.g. "/api/stock/store/transactions/export"
 *   params    — plain object of query params, e.g. { skuId, entryType }
 *   filename  — download file name, e.g. "store-transactions.xlsx"
 *   label     — button text (default: "Export Excel")
 *   className — extra CSS classes for the button
 */
const ExcelDownloadButton = ({
  endpoint,
  params = {},
  filename = "report.xlsx",
  title = "",
  label = "Export Excel",
  className = "",
}) => {
  const token     = useSelector(selectToken);
  const projectId = useSelector(selectActiveProjectId);
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const allParams = title ? { ...params, title } : params;
      const qs = new URLSearchParams(
        Object.fromEntries(Object.entries(allParams).filter(([, v]) => v != null && v !== ""))
      ).toString();
      const url = `${BASE}${endpoint}${qs ? `?${qs}` : ""}`;

      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (projectId) headers["x-project-id"] = projectId;

      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Excel export failed:", err);
      alert("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={`btn btn-sm btn-success d-inline-flex align-items-center gap-1 ${className}`}
      onClick={handleDownload}
      disabled={loading}
      title="Download as Excel"
    >
      {loading ? (
        <>
          <span className="spinner-border spinner-border-sm" />
          Exporting…
        </>
      ) : (
        <>
          <i className="fa fa-file-excel-o" />
          {label}
        </>
      )}
    </button>
  );
};

export default ExcelDownloadButton;
